import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { useSEO } from '../hooks/useSEO';

const BestUnfollowTracker: React.FC = () => {
  useSEO(
    'Best Free Instagram Unfollow Tracker in 2026 (No Login Required) — Purgr',
    'Looking for the safest free Instagram unfollow tracker? We break down your options and explain why browser-based tools that need no login are the only safe choice.'
  );

  return (
    <PageLayout title="Best Free Instagram Unfollow Tracker in 2026">
      <div className="space-y-8 text-white/70 text-sm leading-relaxed">

        <p className="text-white/80 text-base">
          Wanting to know who doesn't follow you back on Instagram is completely normal — but finding
          a tool that does it safely is harder than it should be. Most "unfollow tracker" apps ask for
          your Instagram login or request dangerous permissions. Here's what to look for and what actually
          works in 2026.
        </p>

        {/* What makes a good tracker */}
        <div className="glass-card p-5">
          <p className="text-white/90 font-semibold mb-3 text-base">What to look for in a safe unfollow tracker</p>
          <p className="text-white/60 mb-4">
            Not all unfollow trackers are created equal. Before you hand over access to your account,
            here's what separates a trustworthy tool from a risky one:
          </p>
          <ul className="space-y-3 text-white/55">
            <li className="flex gap-3">
              <span className="text-green-400 flex-shrink-0 font-bold">✓</span>
              <span><strong className="text-white/80">No Instagram login required.</strong> If a tool asks for your username and password,
              walk away. Instagram's terms prohibit sharing credentials with third parties, and doing so
              can get your account suspended or compromised.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-400 flex-shrink-0 font-bold">✓</span>
              <span><strong className="text-white/80">No OAuth or app permissions.</strong> Even "Log in with Instagram" flows grant
              the app access to your account data and can put you at risk of API-related bans.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-400 flex-shrink-0 font-bold">✓</span>
              <span><strong className="text-white/80">Local processing.</strong> The safest tools process your data directly in your browser,
              meaning your Instagram files never travel across the internet to a third-party server.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-400 flex-shrink-0 font-bold">✓</span>
              <span><strong className="text-white/80">Free or transparent pricing.</strong> The best tools in this space are free. Be wary
              of paywalls hiding behind a "free trial" that requires payment info upfront.</span>
            </li>
          </ul>
        </div>

        {/* The problem with most apps */}
        <div>
          <p className="text-white/90 font-semibold mb-3 text-base">Why most unfollow tracker apps are risky</p>
          <p className="text-white/60 mb-3">
            The majority of Instagram unfollow tracking apps work by logging into Instagram on your behalf —
            either by asking for your username and password directly, or by requesting broad OAuth permissions.
            Both approaches carry serious risks:
          </p>
          <div className="space-y-3">
            {[
              {
                title: 'Account suspension',
                body: 'Instagram actively detects and bans accounts that use unauthorized third-party automation. Apps that act on your behalf — liking, following, or scraping data — can trigger automatic suspensions.',
              },
              {
                title: 'Credential theft',
                body: "When you give an app your Instagram password, you're trusting that the app stores it securely and won't misuse it. Many sketchy apps don't — your password can be stored in plaintext, sold, or used to access your account.",
              },
              {
                title: 'Data harvesting',
                body: 'Apps that log into Instagram on your behalf often harvest your follower lists, DMs, and behavior data. This data is frequently sold to advertisers or other third parties.',
              },
            ].map(({ title, body }) => (
              <div key={title} className="glass-card p-4 flex gap-4">
                <span className="text-red-400/70 text-lg flex-shrink-0">⚠️</span>
                <div>
                  <p className="text-white/80 font-semibold text-sm mb-1">{title}</p>
                  <p className="text-white/50 text-xs leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How Purgr works differently */}
        <div>
          <p className="text-white/90 font-semibold mb-3 text-base">How Purgr works — and why it's different</p>
          <p className="text-white/60 mb-3">
            Purgr takes a fundamentally different approach. Instead of logging into Instagram on your behalf,
            it uses Instagram's own data export feature — a built-in tool that lets you download your own data directly from Instagram.
          </p>
          <p className="text-white/60 mb-3">
            You download two files from Instagram's settings (following.json and followers_1.json), then upload
            them to Purgr. The comparison happens entirely in your browser — nothing is sent to any server.
            Purgr never sees your files, and your Instagram account is never touched.
          </p>
          <div className="glass-card p-4 border-green-500/20 bg-green-500/5">
            <p className="text-green-400 font-semibold text-sm mb-2">The result:</p>
            <ul className="space-y-1 text-white/55 text-xs">
              <li>✓ Zero risk of account suspension</li>
              <li>✓ No Instagram login or credentials required</li>
              <li>✓ Your data never leaves your device</li>
              <li>✓ Completely free to use</li>
            </ul>
          </div>
        </div>

        {/* How to use it */}
        <div>
          <p className="text-white/90 font-semibold mb-3 text-base">How to check your non-followers with Purgr</p>
          <div className="space-y-3">
            {[
              { n: '1', title: 'Request your Instagram data export', body: 'Go to Instagram Settings → "Your activity" → "Download your information." Select "Followers and following" under Connections, choose JSON format, and request the download.' },
              { n: '2', title: 'Wait for the email from Instagram', body: 'Instagram will email you when your data is ready — this usually takes a few minutes but can take up to 48 hours for large accounts.' },
              { n: '3', title: 'Upload both files to Purgr', body: 'Unzip your download and find following.json and followers_1.json. Upload both files to Purgr on the home page.' },
              { n: '4', title: 'See your results instantly', body: 'Hit Compare and Purgr will instantly show you who doesn\'t follow you back, who you don\'t follow back, and your mutual followers.' },
            ].map(({ n, title, body }) => (
              <div key={n} className="glass-card p-4 flex gap-4">
                <div className="w-8 h-8 rounded-full ig-gradient-bg flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">
                  {n}
                </div>
                <div>
                  <p className="text-white/80 font-semibold text-sm mb-1">{title}</p>
                  <p className="text-white/50 text-xs leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="rounded-xl p-px ig-gradient-bg">
          <div className="rounded-xl bg-[#0D0D0D] px-6 py-6 text-center">
            <p className="text-white font-bold text-base mb-1">Try Purgr — the safe Instagram unfollow tracker</p>
            <p className="text-white/50 text-sm mb-4">No login. No data uploads. Free forever.</p>
            <Link
              to="/"
              className="btn-gradient inline-block text-white font-bold px-8 py-3 rounded-full"
            >
              Check Who Doesn't Follow You Back →
            </Link>
          </div>
        </div>

        {/* Related guides */}
        <div>
          <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Related guides</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/how-to-download-instagram-data" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to download your Instagram data →</p>
            </Link>
            <Link to="/why-instagram-third-party-apps-are-dangerous" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">Why Instagram third-party apps are risky →</p>
            </Link>
            <Link to="/how-to-see-who-doesnt-follow-you-back" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to see who doesn't follow you back →</p>
            </Link>
            <Link to="/how-to-clean-up-your-following-list" className="glass-card p-4 hover:border-white/20 transition-all">
              <p className="text-white/80 font-medium text-sm">How to clean up your following list →</p>
            </Link>
          </div>
        </div>

      </div>
    </PageLayout>
  );
};

export default BestUnfollowTracker;
